import React, { useEffect, useMemo, useRef, useState } from 'react';
import './styles.scss';

interface DepotLine {
  baseId: number;
  name: string;
  type: string;
  count: number;
}

export interface DepotState {
  holdId: string;
  holdName: string;
  fee: number;
  feeMax: number;
  canSetFee: boolean;
  usersCount: number | null;
  gold: number;
  bag: DepotLine[];
  depot: DepotLine[];
  usage: { types: number; units: number };
  limits: { maxTypes: number; maxUnits: number };
  catalogReady: boolean;
}

type Side = 'bag' | 'depot';

// Browser focus flaps while the overlay is toggled on, so ignore any click for
// a brief window after opening (same guard as ProfessionsMenu/CraftMenu).
const CLICK_GRACE_MS = 250;
// Safety net: a request whose answer never arrives must not lock the buttons.
const BUSY_TIMEOUT_MS = 4000;

const send = (payload: Record<string, unknown>): void => {
  try {
    (window as any).skyrimPlatform.sendMessage(JSON.stringify(payload));
  } catch (e) {
    // Running outside the game (dev preview): log instead.
    // eslint-disable-next-line no-console
    console.log('depot sendMessage', payload);
  }
};

const normalize = (s: string) => s.normalize('NFD').replace(/[̀-ͯ]/g, '').toLowerCase();

const clampQty = (value: number, max: number) => Math.max(1, Math.min(Math.floor(value) || 1, Math.max(1, max)));

const Gauge: React.FC<{ label: string; value: number; max: number }> = ({ label, value, max }) => {
  const ratio = max > 0 ? Math.min(1, value / max) : 0;
  const full = value >= max;
  return (
    <div className="depot-gauge">
      <div className="depot-gauge-head">
        <span className="depot-gauge-label">{label}</span>
        <span className="depot-gauge-value">{value} / {max}</span>
      </div>
      <div className="depot-gauge-track" role="progressbar" aria-valuemin={0} aria-valuemax={max} aria-valuenow={value} aria-label={label}>
        <div className={`depot-gauge-fill${full ? ' depot-gauge-fill--full' : ''}`} style={{ width: `${ratio * 100}%` }} />
      </div>
    </div>
  );
};

const Depot: React.FC = () => {
  const [visible, setVisible] = useState(false);
  const [state, setState] = useState<DepotState | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);
  const [bagQuery, setBagQuery] = useState('');
  const [depotQuery, setDepotQuery] = useState('');
  const [selected, setSelected] = useState<{ side: Side; baseId: number } | null>(null);
  const [qty, setQty] = useState(1);
  const [feeInput, setFeeInput] = useState('0');
  const openedAtRef = useRef(0);
  const busyTimerRef = useRef<number | null>(null);

  const clearBusy = () => {
    if (busyTimerRef.current !== null) {
      window.clearTimeout(busyTimerRef.current);
      busyTimerRef.current = null;
    }
    setBusy(false);
  };

  useEffect(() => {
    const showHandler = (e: Event) => {
      const detail = (e as CustomEvent).detail as { state?: DepotState } | null;
      openedAtRef.current = Date.now();
      setError(null);
      setBagQuery('');
      setDepotQuery('');
      setSelected(null);
      setQty(1);
      clearBusy();
      setState(detail?.state ?? null);
      setFeeInput(String(detail?.state?.fee ?? 0));
      setVisible(true);
    };
    const stateHandler = (e: Event) => {
      const detail = (e as CustomEvent).detail as { state?: DepotState } | null;
      if (!detail?.state) return;
      setError(null);
      setState(detail.state);
      setFeeInput(String(detail.state.fee));
      clearBusy();
    };
    const errorHandler = (e: Event) => {
      const detail = (e as CustomEvent).detail as { text?: string } | null;
      setError(detail?.text || 'Une erreur est survenue.');
      clearBusy();
    };
    const hideHandler = () => setVisible(false);

    window.addEventListener('skymp5-client:showDepot', showHandler);
    window.addEventListener('skymp5-client:depotState', stateHandler);
    window.addEventListener('skymp5-client:depotError', errorHandler);
    window.addEventListener('skymp5-client:hideDepot', hideHandler);
    return () => {
      window.removeEventListener('skymp5-client:showDepot', showHandler);
      window.removeEventListener('skymp5-client:depotState', stateHandler);
      window.removeEventListener('skymp5-client:depotError', errorHandler);
      window.removeEventListener('skymp5-client:hideDepot', hideHandler);
      if (busyTimerRef.current !== null) window.clearTimeout(busyTimerRef.current);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Keep the selection valid when the lists change under it (after an action).
  useEffect(() => {
    if (!selected || !state) return;
    const list = selected.side === 'bag' ? state.bag : state.depot;
    const line = list.find((l) => l.baseId === selected.baseId);
    if (!line) setSelected(null);
    else setQty((q) => clampQty(q, line.count));
  }, [state, selected]);

  const bagLines = useMemo(() => {
    const q = normalize(bagQuery.trim());
    return (state?.bag ?? []).filter((l) => !q || normalize(l.name).includes(q));
  }, [state, bagQuery]);
  const depotLines = useMemo(() => {
    const q = normalize(depotQuery.trim());
    return (state?.depot ?? []).filter((l) => !q || normalize(l.name).includes(q));
  }, [state, depotQuery]);

  if (!visible) return null;

  const close = () => {
    setVisible(false);
    send({ action: 'depotClose' });
  };

  const selectedLine: DepotLine | null = (() => {
    if (!selected || !state) return null;
    const list = selected.side === 'bag' ? state.bag : state.depot;
    return list.find((l) => l.baseId === selected.baseId) ?? null;
  })();

  const roomLeft = state ? Math.max(0, state.limits.maxUnits - state.usage.units) : 0;
  const maxQty = selectedLine
    ? selected?.side === 'bag' ? Math.max(1, Math.min(selectedLine.count, roomLeft)) : selectedLine.count
    : 1;

  const guard = (e: React.MouseEvent) => e.detail !== 0 && Date.now() - openedAtRef.current >= CLICK_GRACE_MS;

  const pick = (e: React.MouseEvent, side: Side, line: DepotLine) => {
    if (!guard(e)) return;
    setSelected({ side, baseId: line.baseId });
    setQty(1);
  };

  const markBusy = () => {
    setBusy(true);
    if (busyTimerRef.current !== null) window.clearTimeout(busyTimerRef.current);
    busyTimerRef.current = window.setTimeout(() => {
      busyTimerRef.current = null;
      setBusy(false);
    }, BUSY_TIMEOUT_MS);
  };

  const act = (e: React.MouseEvent, side: Side) => {
    if (!guard(e) || busy || !selected || selected.side !== side || !selectedLine) return;
    markBusy();
    send({ action: side === 'bag' ? 'depotDeposit' : 'depotWithdraw', baseId: selected.baseId, count: clampQty(qty, maxQty) });
  };

  const saveFee = (e: React.MouseEvent) => {
    if (!guard(e) || busy || !state) return;
    const fee = Math.floor(Number(feeInput));
    if (!Number.isFinite(fee) || fee < 0 || fee > state.feeMax) return;
    markBusy();
    send({ action: 'depotSetFee', fee });
  };

  const feeValid = (() => {
    if (!state) return false;
    const n = Number(feeInput);
    return feeInput.trim() !== '' && Number.isFinite(n) && n >= 0 && n <= state.feeMax;
  })();

  const renderList = (side: Side, lines: DepotLine[], total: number, emptyText: string) => (
    <div className="depot-list" role="listbox" aria-label={side === 'bag' ? 'Dans votre sac' : 'Dans l\'entrepôt'}>
      {lines.length === 0 ? (
        <div className="depot-empty">{total === 0 ? emptyText : 'Aucun objet ne correspond à la recherche.'}</div>
      ) : (
        lines.map((l) => {
          const isSel = selected?.side === side && selected.baseId === l.baseId;
          return (
            <button
              type="button"
              key={l.baseId}
              role="option"
              aria-selected={isSel}
              className={`depot-row${isSel ? ' depot-row--selected' : ''}`}
              onClick={(e) => pick(e, side, l)}
            >
              <span className="depot-row-name">{l.name}</span>
              <span className="depot-row-count">{l.count}</span>
            </button>
          );
        })
      )}
    </div>
  );

  return (
    <div className="depot-overlay">
      <div className="depot-panel">
        <div className="depot-header">
          <div className="depot-header-title">Entrepôt du fief</div>
          {state && <div className="depot-header-hold">{state.holdName}</div>}
        </div>

        {error ? (
          <div className="depot-body depot-body--message">
            <div className="depot-message depot-message--error">{error}</div>
          </div>
        ) : !state ? (
          <div className="depot-body depot-body--message">
            <div className="depot-message">Chargement de l'entrepôt…</div>
          </div>
        ) : (
          <div className="depot-body">
            <div className="depot-gauges">
              <Gauge label="Unités" value={state.usage.units} max={state.limits.maxUnits} />
              <Gauge label="Types d'objets" value={state.usage.types} max={state.limits.maxTypes} />
            </div>

            <div className="depot-info">
              <span className="depot-info-fee">
                {state.fee > 0 ? `Frais de dépôt : ${state.fee} Septims par dépôt` : 'Dépôt gratuit dans ce fief'}
              </span>
              {state.usersCount !== null && (
                <span className="depot-info-users">
                  {state.usersCount} utilisateur{state.usersCount > 1 ? 's' : ''} de l'entrepôt
                </span>
              )}
            </div>

            {state.canSetFee && (
              <div className="depot-feeedit">
                <label className="depot-feeedit-label" htmlFor="depot-fee-input">
                  Frais par dépôt (0 à {state.feeMax} Septims)
                </label>
                <input
                  id="depot-fee-input"
                  className="depot-input depot-input--fee"
                  type="number"
                  min={0}
                  max={state.feeMax}
                  step={1}
                  value={feeInput}
                  onChange={(e) => setFeeInput(e.target.value)}
                />
                <button
                  type="button"
                  className="depot-button depot-button--ghost"
                  disabled={busy || !feeValid || Number(feeInput) === state.fee}
                  onClick={saveFee}
                >
                  Enregistrer
                </button>
              </div>
            )}

            <div className="depot-columns">
              <section className="depot-column">
                <div className="depot-column-title">Dans votre sac</div>
                <input
                  className="depot-input"
                  type="search"
                  value={bagQuery}
                  onChange={(e) => setBagQuery(e.target.value)}
                  placeholder="Rechercher"
                  aria-label="Rechercher dans votre sac"
                  autoComplete="off"
                />
                {renderList(
                  'bag',
                  bagLines,
                  state.bag.length,
                  state.catalogReady
                    ? 'Rien à déposer. Seuls les objets simples non équipés sont acceptés.'
                    : 'Chargement du catalogue des objets…'
                )}
              </section>

              <section className="depot-column">
                <div className="depot-column-title">Dans l'entrepôt de {state.holdName}</div>
                <input
                  className="depot-input"
                  type="search"
                  value={depotQuery}
                  onChange={(e) => setDepotQuery(e.target.value)}
                  placeholder="Rechercher"
                  aria-label="Rechercher dans l'entrepôt"
                  autoComplete="off"
                />
                {renderList('depot', depotLines, state.depot.length, "L'entrepôt est vide.")}
              </section>
            </div>

            <div className="depot-actions">
              <div className="depot-selection">
                {selectedLine ? (
                  <>
                    <span className="depot-selection-name">{selectedLine.name}</span>
                    <span className="depot-selection-hint">
                      {selected?.side === 'bag' ? 'à déposer' : 'à retirer'}
                    </span>
                  </>
                ) : (
                  <span className="depot-selection-hint">Choisis un objet dans l'une des deux listes.</span>
                )}
              </div>

              <div className="depot-stepper" aria-label="Quantité">
                <button
                  type="button"
                  className="depot-stepper-btn"
                  aria-label="Diminuer la quantité"
                  disabled={!selectedLine || qty <= 1}
                  onClick={() => setQty((q) => clampQty(q - 1, maxQty))}
                >
                  −
                </button>
                <input
                  className="depot-stepper-input"
                  type="number"
                  min={1}
                  max={maxQty}
                  value={qty}
                  disabled={!selectedLine}
                  aria-label="Quantité"
                  onChange={(e) => setQty(clampQty(Number(e.target.value), maxQty))}
                />
                <button
                  type="button"
                  className="depot-stepper-btn"
                  aria-label="Augmenter la quantité"
                  disabled={!selectedLine || qty >= maxQty}
                  onClick={() => setQty((q) => clampQty(q + 1, maxQty))}
                >
                  +
                </button>
                <button
                  type="button"
                  className="depot-button depot-button--ghost depot-button--sm"
                  disabled={!selectedLine}
                  onClick={() => setQty(maxQty)}
                >
                  Tout
                </button>
              </div>

              <div className="depot-actions-buttons">
                <button
                  type="button"
                  className={`depot-button${selected?.side === 'bag' ? '' : ' depot-button--ghost'}`}
                  disabled={busy || !selectedLine || selected?.side !== 'bag'}
                  onClick={(e) => act(e, 'bag')}
                >
                  Déposer{state.fee > 0 ? ` (${state.fee} Septims)` : ''}
                </button>
                <button
                  type="button"
                  className={`depot-button${selected?.side === 'depot' ? '' : ' depot-button--ghost'}`}
                  disabled={busy || !selectedLine || selected?.side !== 'depot'}
                  onClick={(e) => act(e, 'depot')}
                >
                  Retirer
                </button>
              </div>
            </div>
          </div>
        )}

        <div className="depot-close-row">
          <div className="depot-hint">
            <kbd>Échap</kbd> Fermer
          </div>
          <button type="button" className="depot-button depot-button--quiet" onClick={close}>
            Fermer
          </button>
        </div>
      </div>
    </div>
  );
};

export default Depot;
